import { Flex, Field } from "@chakra-ui/react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

import { useTranslation } from "react-i18next";
import { ErrorMessage } from "@digital-services-gard-as/anchor-react";
import { InvoiceFromOcrDto, getAttachment } from "@/api/invoiceApi";
import {
    deleteAttachmentMutationOptions,
    ocrScanInvoiceAttachmentMutationOptions,
    uploadAttachmentByPostMutationOptions
} from "@/api/queryOptions/invoiceAPIQueryOptions";
import InvoiceFileUpload from "@/components/InvoiceFileUpload";
import { InvoiceFormData, InvoiceDetailsDto } from "@/types";
import { ClaimRefBanner } from "@/app/invoice/[invoiceId]/_components/createInvoice/invoiceAttachmentSection/ClaimRefBanner";
import { PreFilledBanner } from "@/app/invoice/[invoiceId]/_components/createInvoice/invoiceAttachmentSection/PreFilledInformation";

type InvoiceAttachmentSectionProps = {
    invoiceId: string;
    markFormChanged: () => void;
    isLoadingOcr?: boolean;
    ocrResponse?: InvoiceFromOcrDto;
};

export const InvoiceAttachmentSection = ({
    invoiceId,
    markFormChanged,
    isLoadingOcr,
    ocrResponse
}: InvoiceAttachmentSectionProps) => {
    const { t } = useTranslation(["invoices"]);
    const queryClient = useQueryClient();
    const {
        getValues,
        setValue,
        formState: { errors },
        watch
    } = useFormContext<InvoiceFormData>();
    const attachment = watch("invoiceAttachment");

    const { mutate: deleteAttachment } = useMutation(deleteAttachmentMutationOptions);
    const { mutate: uploadAttachment } = useMutation(uploadAttachmentByPostMutationOptions);
    const { mutate: ocrScanAttachment } = useMutation(ocrScanInvoiceAttachmentMutationOptions);

    const handleDeleteClick = useCallback(() => {
        const file = getValues().invoiceAttachment;
        if (!file) {
            return;
        }
        if (file.docId) {
            deleteAttachment(
                { id: invoiceId },
                {
                    onSuccess: () => {
                        queryClient.setQueryData(["invoice", invoiceId], (oldData: InvoiceDetailsDto | undefined) => {
                            if (!oldData) {
                                return oldData;
                            }
                            return { ...oldData, attachment: null };
                        });
                    }
                }
            );
        }
        setValue("invoiceAttachment", undefined);
        markFormChanged();
    }, [getValues, setValue, markFormChanged, deleteAttachment, invoiceId, queryClient]);

    const handleAttachmentClick = useCallback((invoiceIdentifier: string, docId: string | undefined) => {
        if (docId === undefined) {
            return;
        }
        getAttachment(invoiceIdentifier, docId);
    }, []);

    const updateFormAttachment = useCallback(
        (
            updater: (prev: NonNullable<InvoiceFormData["invoiceAttachment"]>) => InvoiceFormData["invoiceAttachment"]
        ) => {
            const current = getValues().invoiceAttachment;
            if (!current) {
                return;
            }
            setValue("invoiceAttachment", updater(current));
        },
        [getValues, setValue]
    );

    const markUploadFailed = useCallback(() => {
        updateFormAttachment(prev => ({
            ...prev,
            isUploading: false,
            isUploaded: false,
            uploadFailed: true
        }));
    }, [updateFormAttachment]);

    const handleAttachmentUploaded = useCallback(
        (attachmentId: string) => {
            ocrScanAttachment(attachmentId, {
                onSettled: () => {
                    updateFormAttachment(prev => ({
                        ...prev,
                        isUploading: false,
                        isUploaded: true,
                        uploadFailed: false,
                        docId: attachmentId
                    }));

                    const uploadedAttachment = getValues().invoiceAttachment;
                    if (uploadedAttachment) {
                        queryClient.setQueryData(["invoice", invoiceId], (oldData: InvoiceDetailsDto | undefined) => {
                            if (!oldData) {
                                return oldData;
                            }
                            return {
                                ...oldData,
                                attachment: {
                                    documentId: attachmentId,
                                    filename: uploadedAttachment.filename,
                                    fileExtension: uploadedAttachment.fileExtension ?? "",
                                    fileSize: uploadedAttachment.fileSize
                                }
                            };
                        });
                    }
                }
            });
        },
        [getValues, invoiceId, ocrScanAttachment, queryClient, updateFormAttachment]
    );

    const handleUpload = useCallback(
        (file: File) => {
            updateFormAttachment(prev => ({
                ...prev,
                isUploading: true,
                isUploaded: false,
                uploadFailed: false
            }));

            const form = new FormData();
            form.append("formFile", file);
            form.append("filename", file.name);

            uploadAttachment(
                {
                    id: invoiceId,
                    form
                },
                {
                    onSuccess: handleAttachmentUploaded,
                    onError: markUploadFailed
                }
            );
        },
        [handleAttachmentUploaded, invoiceId, markUploadFailed, updateFormAttachment, uploadAttachment]
    );

    return (
        <Field.Root invalid={Boolean(errors.invoiceAttachment?.message)} w="100%">
            <Flex
                flexDir="column"
                w="100%"
                backgroundColor="white"
                p="1.5rem"
                borderRadius="0.5rem"
                boxShadow="xs"
                gap="1.5rem"
            >
                <>
                    {!attachment && (
                        <Field.Label mb="1rem">{t("createInvoice.feesAndExpenses.attachInvoice")}</Field.Label>
                    )}
                    <InvoiceFileUpload
                        id="invoiceAttachment"
                        onUpload={handleUpload}
                        name={"invoiceAttachment"}
                        invoiceId={invoiceId}
                        onDeleteClick={handleDeleteClick}
                        onAttachmentClick={handleAttachmentClick}
                        accept={{
                            "image/jpeg": [".png", ".jpg", ".jpeg", ".tiff", ".tif", ".gif"],
                            "application/pdf": [".pdf"]
                        }}
                        isInvalid={Boolean(errors.invoiceAttachment?.message)}
                        isLoadingOcr={isLoadingOcr}
                    />
                </>
                <PreFilledBanner ocrResponse={ocrResponse} />
                <ClaimRefBanner ocrResponse={ocrResponse} />
            </Flex>
            <ErrorMessage size="sm" data-testid="invoiceAttachmentError">
                {errors.invoiceAttachment?.message}
            </ErrorMessage>
        </Field.Root>
    );
};
